const SETTLE_TOLERANCE_PX = 2;
const MAX_SCROLL_ATTEMPTS = 3;
const IDLE_POLL_MS = 100;
const MAX_IDLE_WAIT_MS = 3000;
const HOLD_POLL_MS = 100;
const MAX_HOLD_MS = 2000;

let latestRunId = 0;

const sleep = (ms: number) => new Promise<void>((resolve) => globalThis.setTimeout(resolve, ms));

const waitForScrollIdle = async (isStale: () => boolean) => {
  let lastY = globalThis.scrollY;
  for (let elapsed = 0; elapsed < MAX_IDLE_WAIT_MS; elapsed += IDLE_POLL_MS) {
    await sleep(IDLE_POLL_MS);
    if (isStale()) return;
    const y = globalThis.scrollY;
    if (Math.abs(y - lastY) < 1) return;
    lastY = y;
  }
};

const getTargetTop = (el: HTMLElement) => Number.parseFloat(globalThis.getComputedStyle(el).scrollMarginTop) || 0;

const holdAtTarget = async (el: HTMLElement, isStale: () => boolean) => {
  for (let elapsed = 0; elapsed < MAX_HOLD_MS; elapsed += HOLD_POLL_MS) {
    await sleep(HOLD_POLL_MS);
    if (isStale()) return;
    if (Math.abs(el.getBoundingClientRect().top - getTargetTop(el)) <= SETTLE_TOLERANCE_PX) continue;

    const startY = globalThis.scrollY;
    el.scrollIntoView({ behavior: 'auto', block: 'start' });
    if (Math.abs(globalThis.scrollY - startY) <= SETTLE_TOLERANCE_PX) return;
  }
};

export const scrollIntoViewSettled = async (el: HTMLElement): Promise<void> => {
  const runId = ++latestRunId;
  let interrupted = false;
  const interrupt = () => {
    interrupted = true;
  };
  const isStale = () => interrupted || runId !== latestRunId;
  const behavior = globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

  globalThis.addEventListener('wheel', interrupt, { passive: true });
  globalThis.addEventListener('touchstart', interrupt, { passive: true });
  globalThis.addEventListener('keydown', interrupt, { passive: true });
  globalThis.addEventListener('pointerdown', interrupt, { passive: true });

  try {
    for (let attempt = 0; attempt < MAX_SCROLL_ATTEMPTS; attempt++) {
      const startY = globalThis.scrollY;
      el.scrollIntoView({ behavior, block: 'start' });
      await waitForScrollIdle(isStale);
      if (isStale()) return;

      if (Math.abs(el.getBoundingClientRect().top - getTargetTop(el)) <= SETTLE_TOLERANCE_PX) break;
      if (attempt > 0 && Math.abs(globalThis.scrollY - startY) <= SETTLE_TOLERANCE_PX) break;
    }

    await holdAtTarget(el, isStale);
  } finally {
    globalThis.removeEventListener('wheel', interrupt);
    globalThis.removeEventListener('touchstart', interrupt);
    globalThis.removeEventListener('keydown', interrupt);
    globalThis.removeEventListener('pointerdown', interrupt);
  }
};
